import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth.hook";
import { Button, Input, GoogleButton } from "@/components/atoms";
import { FormField } from "@/components/molecules/FormField";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { SIGNUP_SEO } from "@/constants/site-seo";
import { applyPageMeta } from "@/utils/page-meta.utils";

export function SignUpPage() {
  const { signUp, signUpWithGoogle, signInWithGoogle, loading } = useAuth();
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    startupName: "",
    initialFunding: "",
  });
  const [error, setError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    applyPageMeta({
      title: SIGNUP_SEO.title,
      description: SIGNUP_SEO.description,
      canonicalPath: "/signup",
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      await signUp({
        displayName: formData.displayName,
        email: formData.email,
        password: formData.password,
        startupName: formData.startupName || undefined,
        initialFunding: formData.initialFunding
          ? Number(formData.initialFunding)
          : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign up failed");
    }
  };

  const handleGoogleSignUp = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      const startupData = {
        startupName: formData.startupName || undefined,
        initialFunding: formData.initialFunding
          ? Number(formData.initialFunding)
          : undefined,
      };
      if (formData.startupName?.trim()) {
        await signUpWithGoogle(startupData);
      } else {
        await signInWithGoogle();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Google sign up failed";
      setError(message);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <AuthLayout
      title="Create account"
      subtitle="Model your runway in minutes — add your first startup now or after sign-in."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/signin" className="font-semibold text-brand hover:text-brand-muted">
            Sign in
          </Link>
        </>
      }
    >
      <div className="sh-card p-6">
        <form className="space-y-5" onSubmit={handleSubmit}>
          {error ? (
            <div className="rounded-[14px] border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
              {error}
            </div>
          ) : null}

          <FormField label="Full name" required>
            <Input
              type="text"
              placeholder="Jane Founder"
              value={formData.displayName}
              onChange={(e) => handleChange("displayName", e.target.value)}
              required
            />
          </FormField>

          <FormField label="Email" required>
            <Input
              type="email"
              placeholder="you@startup.com"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              required
            />
          </FormField>

          <FormField label="Password" required helperText="At least 6 characters">
            <Input
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              required
            />
          </FormField>

          <FormField label="Confirm password" required>
            <Input
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              required
            />
          </FormField>

          <div className="border-t border-white/10 pt-5">
            <p className="sh-kicker">Optional — first startup</p>
            <p className="mt-1 text-xs text-white/50">
              You can skip this and set up later from the dashboard.
            </p>
            <div className="mt-4 space-y-4">
              <FormField label="Startup name">
                <Input
                  type="text"
                  placeholder="Acme Inc"
                  value={formData.startupName}
                  onChange={(e) => handleChange("startupName", e.target.value)}
                />
              </FormField>
              <FormField label="Initial funding (USD)">
                <Input
                  type="number"
                  placeholder="100000"
                  value={formData.initialFunding}
                  onChange={(e) => handleChange("initialFunding", e.target.value)}
                />
              </FormField>
            </div>
          </div>

          <Button variant="primary" size="lg" type="submit" loading={loading} className="w-full">
            Create account
          </Button>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-surface px-2 text-white/45">Or continue with</span>
            </div>
          </div>

          <GoogleButton onClick={handleGoogleSignUp} loading={googleLoading}>
            Continue with Google
          </GoogleButton>
        </form>
      </div>
    </AuthLayout>
  );
}
